/**
 * The frame the plainer landing sections sit in: a band holding a column of
 * words (the gardener's pain in a bubble, the heading with its icon to its
 * left, and what follows) and, beside it on a wide screen, the picture or
 * example that shows it. A section that has no visual, or one that is not
 * shown, uses the words alone. Its heading and spacing match the feature
 * bands (FeatureBlock), which also use its PainBubble.
 *
 * Sections do not draw their own bands, so moving one in the order
 * (landingSections) needs nothing else changed.
 *
 * Used by: sections/FullLife.tsx and sections/Price.tsx; PainBubble by
 * components/FeatureBlock.tsx.
 *
 * @module LandingBlock
 */

import React from 'react';
import { cn } from '@/lib/utils';

/** The parts of one block. */
interface LandingBlockProps {
  /** An icon to the left of the heading. */
  icon?: React.ReactNode;
  /** A small line above the heading (the remodelled price section's off-season pill). */
  eyebrow?: React.ReactNode;
  /** What the gardener says, in their words; shown in a light sage bubble. */
  pain?: string;
  /** The heading. */
  title: string;
  /** What follows the heading: paragraphs, a button. */
  children?: React.ReactNode;
  /** The example or picture beside the words. */
  visual?: React.ReactNode;
  /** Keeps the words and visual in one column even on a wide screen. */
  single?: boolean;
}

/**
 * A light sage bubble holding what the gardener says.
 *
 * @param props.children - Their words, without quotation marks.
 */
export const PainBubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="inline-block max-w-prose rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-lg italic leading-snug text-foreground">
    &ldquo;{children}&rdquo;
  </p>
);

/**
 * A landing section's frame.
 *
 * @param props - See LandingBlockProps.
 */
export const LandingBlock: React.FC<LandingBlockProps> = ({ icon, eyebrow, pain, title, children, visual, single = false }) => (
  <section className="px-4 py-12 md:px-8 md:py-20">
    <div className={cn('mx-auto grid max-w-5xl gap-6 md:gap-10', !single && 'md:grid-cols-2 md:items-center')}>
      <div className="min-w-0 space-y-4">
        {eyebrow}
        {pain && <PainBubble>{pain}</PainBubble>}
        <div className="flex items-start gap-3">
          {icon && <div className="shrink-0 text-primary" aria-hidden="true">{icon}</div>}
          <h2 className="min-w-0 font-grow text-3xl font-extrabold leading-[1.08] tracking-tight [text-wrap:balance] md:text-[2.6rem]">{title}</h2>
        </div>
        {children}
      </div>
      {visual && <div className="min-w-0 space-y-3 empty:hidden">{visual}</div>}
    </div>
  </section>
);
